import PersonIcon from '@mui/icons-material/Person';
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { updateUser } from 'lib/api';
import { assignRoleToUser } from 'lib/api/users/roles';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import Title from 'lib/designSystem/Title';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import { Role } from 'lib/types/models';
import React from 'react';
import { useDispatch } from 'react-redux';

import { UserInterface } from '../..';
import { nullUserState } from '../..';

interface Props {
  handleCloseDialog: () => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  openEditDialog: boolean;
  editType: 'password' | 'info';
  fetchUsers: () => Promise<void>;
  roles: Role[];
  setRoles: React.Dispatch<React.SetStateAction<Role[]>>;
  selectedUser: UserInterface;
  setSelectedUser: React.Dispatch<React.SetStateAction<UserInterface>>;
}

const EditUserDialog: React.FC<Props> = ({
  handleCloseDialog,
  loading,
  setLoading,
  openEditDialog,
  editType,
  fetchUsers,
  roles,
  setSelectedUser,
  selectedUser,
}) => {
  const dispatch = useDispatch();

  const handleSaveEditDialog = async () => {
    try {
      setLoading(true);
      const payload = {
        id: selectedUser.id,
        name: selectedUser.name,
        ...(selectedUser.password?.length && {
          password: selectedUser.password,
        }),
      };
      await updateUser(dispatch, payload);
      setSelectedUser(nullUserState);
      await fetchUsers();
      handleCloseDialog();
      showAlert(dispatch, 'success', 'Se ha editado el usuario.');
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAssignationsEditDialog = async () => {
    try {
      setLoading(true);
      const rolesIds = (selectedUser.rolesNames || [])
        .map(
          (roleName: string) =>
            roles.find((role: Role) => role.name === roleName)?.id,
        )
        .filter((id): id is number => typeof id === 'number');
      const payload = {
        id: selectedUser.id,
        rolesIds,
      };
      await assignRoleToUser(dispatch, payload);
      setSelectedUser(nullUserState);
      await fetchUsers();
      handleCloseDialog();
      showAlert(
        dispatch,
        'success',
        'Se ha editado el usuario. Recuerda que con esto cambian las notificaciones, para revisarlas el usuario puede ir a su perfil.',
      );
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedUser((prev: UserInterface) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string[]>) => {
    const { name, value } = e.target;
    setSelectedUser((prev: UserInterface) => ({
      ...prev,
      [name]: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  if (loading) return <CenteredCircularProgress />;

  return (
    <Dialog fullWidth open={openEditDialog} onClose={() => handleCloseDialog()}>
      <DialogTitle>
        <Title
          title={`Editar ${selectedUser.email}`}
          icon={<PersonIcon color="primary" />}
          color="primary"
        />
      </DialogTitle>
      {editType === 'password' ? (
        <DialogContent>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                name="name"
                label="Nombre"
                fullWidth
                value={selectedUser.name}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                name="password"
                label="Contraseña"
                value={selectedUser.password}
                onChange={handleInputChange}
                type="password"
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
      ) : (
        <DialogContent style={{ paddingTop: 20 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-mutiple-checkbox-label">Permisos</InputLabel>
            <Select
              labelId="demo-mutiple-checkbox-label"
              id="demo-mutiple-checkbox"
              label="Permisos"
              multiple
              value={selectedUser.rolesNames}
              name="rolesNames"
              onChange={handleSelectChange}
              input={<Input />}
              renderValue={(selected) => selected.join(', ')}
            >
              {roles.map((role: Role) => (
                <MenuItem key={role.id} value={role.name}>
                  <Checkbox
                    checked={(selectedUser.rolesNames || []).includes(
                      role.name,
                    )}
                  />
                  <ListItemText primary={role.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
      )}
      <DialogActions>
        <CustomButton
          color="primary"
          onClick={() =>
            editType === 'password'
              ? handleSaveEditDialog()
              : handleSaveAssignationsEditDialog()
          }
        >
          Guardar
        </CustomButton>
        <CustomButton onClick={() => handleCloseDialog()} color="secondary">
          Salir
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;
